<template>
  <div
    v-bind="$attrs"
    class="grid gap-2 border-b p-6 items-center text-gray-500"
    @click="toggleSection"
  >
    <div class="flex w-full items-center space-x-2 bg-white">
      <lf-switch
        v-if="!props.isFunderList"
        class="flex-none"
        name="template-toggle"
        :model-value="currentOfferTemplateWfbRecord?.enabled"
        :disabled="!canCustomiseWfbRecord"
        @update:model-value="handleToggleTemplate"
      />
      <div v-tooltipable class="max-w-28 truncate text-nowrap md:max-w-32">
        {{ templateName }}
      </div>
    </div>
    <div class="bg-white">
      {{ PRODUCT_TYPE_OPTIONS[localOfferTemplate.product] }}
    </div>
    <div v-if="showPresetDropdowns">
      <lf-dropdown-with-api-search
        :value="slectedWorkflowTemplate?.name"
        :placeholder="`${$t('COMMON.SELECT')} ${$t('COMMON.TEMPLATE')}`"
        :search-term="$t('WORKFLOW.TEMPLATES', 1)"
        :get-paginated-options="getWorkflowTemplates"
        name="client_workflow_template"
        accessor="name"
        as-badge
        include-none
        :show-warning-icon="false"
        :disabled="!canCustomiseWfbRecord"
        full-width
        @click.stop
        @change="setDefaultWFBTemplate"
      />
    </div>
    <div v-if="showPresetDropdowns">
      <funder-product-dropdown
        :value="selectedOfferTemplate?.name"
        :model="funder"
        :none-option="{ value: 0, label: $t('COMMON.NONE') }"
        :linked-wfb-template-id="
          currentOfferTemplateWfbRecord?.destinations[0]?.workflow_template
        "
        :disabled="
          !currentOfferTemplateWfbRecord?.destinations[0]?.workflow_template ||
          !canCustomiseWfbRecord
        "
        as-badge
        @change="setDefaultOfferTemplate"
      />
    </div>
    <div
      v-if="
        hasPlacementStage ||
        activeTemplate.managed_by_lendflow ||
        props.isFunderList
      "
    >
      <span v-if="localOfferTemplate.client" v-tooltipable class="line-clamp-2">
        {{ localOfferTemplate.client?.name }}
      </span>

      <div v-else>
        <icon-base
          :icon="IconLogo"
          height="20"
          width="30"
          view-box="0 0 20 30"
        />
      </div>
    </div>

    <div v-tooltipable class="w-full truncate pr-4 bg-white">
      {{ localOfferTemplate.description }}
    </div>

    <div class="w-full bg-white">
      <date-and-time-display :value="localOfferTemplate.updated_at" />
    </div>

    <div class="flex w-full h-full items-center space-x-2 bg-white">
      <wfb-duplicate-offer-template
        v-if="
          showDuplicateButton &&
          (canCreateOfferTemplate || props.lendflowManagedView)
        "
        :template-id="localOfferTemplate.id"
        :client-id="clientId"
        class="cursor-pointer"
        @template-created="$emit('template-created', $event)"
      />
      <div>
        <icon-base
          v-tooltip="$t('OFFER_CUSTOMISATION.EDIT_PRODUCT_TEMPLATES')"
          :icon="IconCogWheel"
          view-box="4 4 16 16"
          :class="[
            { 'text-blue-500': isSectionOpened },
            !canCustomiseWfbRecord ? 'cursor-not-allowed' : 'cursor-pointer',
          ]"
        />
      </div>
      <div v-if="props.isFunderList && canDeleteOfferTemplate">
        <icon-base
          v-tooltip="$t('OFFER_CUSTOMISATION.DELETE_PRODUCT_TEMPLATES')"
          :icon="IconDelete"
          data-cy="delete-offer-template-list-item"
          width="16"
          height="16"
          view-box="0 0 16 16"
          class="flex-none cursor-pointer text-label"
          @click.stop="deleteOfferTemplate"
        />
      </div>
    </div>
  </div>
  <div v-if="isSectionOpened" class="bg-gray-50 p-3">
    <loader :is-loading="isContentLoading" />
    <div
      v-if="localOfferTemplate.content && !isContentLoading"
      class="flex gap-3"
    >
      <wfb-offer-section-card
        :title="$t('OFFER_CUSTOMISATION.PRODUCT_DESCRIPTION')"
        @open-edit-modal="isDescriptionModalOpen = true"
      >
        <div class="flex flex-col space-y-1">
          <div class="space-x-1 text-xs font-semibold">
            <span>{{ PRODUCT_TYPE_OPTIONS[localOfferTemplate.product] }}</span>
            <span>•</span>
            <span>{{ localOfferTemplate.name }}</span>
          </div>
          <div class="max-h-44 w-full overflow-y-auto break-all">
            {{ localOfferTemplate.description }}
          </div>
        </div>
      </wfb-offer-section-card>
      <wfb-offer-section-card
        :title="$t('OFFER_CUSTOMISATION.OFFER_FIELDS')"
        @open-edit-modal="showOfferFieldsModal = true"
      >
        <badge-list :value="offerTemplateFields" :show-num-of-items="5" />
      </wfb-offer-section-card>
      <wfb-offer-section-card
        :title="$t('STIPS.STIP')"
        @open-edit-modal="() => (showAddStipModal = true)"
      >
        <badge-list :value="stipNames" :show-num-of-items="5" />
      </wfb-offer-section-card>
      <wfb-offer-section-card
        v-if="showCheckout"
        :title="$t('CHECKOUT.CHECKOUT')"
        @open-edit-modal="() => (showCheckoutModal = true)"
      >
        <div>
          {{ selectedCheckoutTemplate?.name ?? "-" }}
        </div>
      </wfb-offer-section-card>
    </div>
  </div>
  <offer-template-description-modal
    v-if="isDescriptionModalOpen"
    :offer-template="localOfferTemplate"
    :current-funder="funder"
    is-edit
    @close="isDescriptionModalOpen = false"
    @template-updated="updateOfferTemplate"
  />
  <offer-stips-modal
    v-if="showAddStipModal"
    :stips="selectedStips"
    :title="$t('DEALS.DEAL_PROGRESS.SET_REQUIRED_STIPS')"
    :offer-template="localOfferTemplate"
    :funder="funder"
    is-select-only
    @close="showAddStipModal = false"
    @stips-updated="updateOfferTemplateStips"
  />
  <wfb-offer-fields-modal
    v-if="showOfferFieldsModal"
    :template="localOfferTemplate"
    :funder="funder"
    @close="() => (showOfferFieldsModal = false)"
    @template-updated="updateOfferTemplate"
  />
  <offer-checkout-modal
    v-if="!!funder && showCheckoutModal"
    :checkout-templates="checkoutTemplatesForFunder"
    :offer-template="localOfferTemplate"
    :funder="funder"
    @close="() => (showCheckoutModal = false)"
    @template-updated="updateOfferTemplate"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import { Ability, PermissionSubject } from "@/enums/auth";
import { PRODUCT_TYPE_OPTIONS } from "@/helpers/constants/offers";
import { useAuth, usePermissions } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useStipsIndexQuery } from "@/hooks/queries/stips";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";
import checkoutTemplateService from "@/services/modules/checkoutTemplates";
import offerTemplateService from "@/services/modules/offerTemplate";
import workflowApiService from "@/services/modules/workflows";

import FunderProductDropdown from "@/components/deals/placement/FunderProductDropdown.vue";
import OfferCheckoutModal from "@/components/deals/workflow/OfferStage/OfferCheckoutModal.vue";
import OfferStipsModal from "@/components/deals/workflow/OfferStage/OfferStipsModal.vue";
import OfferTemplateDescriptionModal from "@/components/deals/workflow/OfferStage/OfferTemplateDescriptionModal.vue";
import WfbDuplicateOfferTemplate from "@/components/deals/workflow/OfferStage/WfbDuplicateOfferTemplate.vue";
import WfbOfferFieldsModal from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/WfbOfferFieldsModal.vue";
import WfbOfferSectionCard from "@/components/deals/workflow/OfferStage/WfbOfferSectionCard.vue";
import IconCogWheel from "@/components/icons/IconCogWheel.vue";
import IconDelete from "@/components/icons/IconDelete.vue";
import IconLogo from "@/components/icons/IconLogo.vue";
import BadgeList from "@/components/ui/BadgeList.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";
import LfSwitch from "@/components/ui/inputs/LfSwitch.vue";
import DateAndTimeDisplay from "@/components/ui/text/DateAndTimeDisplay.vue";

import type { CheckoutTemplateBase } from "@/models/checkout";
import type { SearchParams } from "@/models/common";
import type { IFunder } from "@/models/funders";
import type { OfferTemplate } from "@/models/offers";
import type { StipDefinition } from "@/models/stips";
import type {
  IWorkflow,
  WfbFunderRecord,
  WfbOfferTemplateRecord,
  WorkflowTemplatesPayloadParams,
} from "@/models/workflows";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  offerTemplate: OfferTemplate;
  funder?: IFunder;
  clientId?: string | null;
  funderWfbRecord?: WfbFunderRecord;
  lendflowManagedView?: boolean;
  showDuplicateButton?: boolean;
  isFunderList?: boolean;
}>();

const emit = defineEmits<{
  "template-created": [OfferTemplate];
  "template-deleted": [OfferTemplate];
}>();

const { canPerformActionReactive } = usePermissions();
const auth = useAuth();
const { data: stips, isLoading: isStipsLoading } = useStipsIndexQuery();

const canUpdateOfferTemplate = canPerformActionReactive(
  PermissionSubject.offerTemplate,
  Ability.update,
  { offerTemplate: props.offerTemplate.id }
);

const canCreateOfferTemplate = canPerformActionReactive(
  PermissionSubject.offerTemplate,
  Ability.create
);

const canDeleteOfferTemplate = canPerformActionReactive(
  PermissionSubject.offerTemplate,
  Ability.delete,
  { offerTemplate: props.offerTemplate.id }
);

const { hasStageTypeComputed, activeTemplate } = useActiveWorkflowTemplate();
const { dispatch } = useStore();

const showOfferFieldsModal = ref(false);
const isSectionOpened = ref(false);
const isDescriptionModalOpen = ref(false);
const showAddStipModal = ref(false);
const showCheckoutModal = ref(false);
const slectedWorkflowTemplate = ref<IWorkflow | null>(null);
const checkoutTemplatesForFunder = ref<CheckoutTemplateBase[]>([]);

const localOfferTemplate = ref<OfferTemplate>(props.offerTemplate);
const selectedOfferTemplate = ref<Partial<OfferTemplate> | null | undefined>(
  null
);

const templateName = computed(() => localOfferTemplate.value.name);

const selectedCheckoutTemplate = computed(() =>
  checkoutTemplatesForFunder.value.find(
    (checkoutTemplate) =>
      checkoutTemplate.id ===
      localOfferTemplate.value.content.checkout_template_id
  )
);

const canCustomiseWfbRecord = computed(() => {
  return (
    ((props.lendflowManagedView || !activeTemplate.value.client?.id) &&
      auth.isLendflowUser &&
      canUpdateOfferTemplate.value) ||
    (canUpdateOfferTemplate.value &&
      !props.lendflowManagedView &&
      !auth.isLendflowUser)
  );
});

const hasPlacementStage = hasStageTypeComputed("placement");

const showPresetDropdowns = computed(() => !!hasPlacementStage.value);

const idToStipMap = computed(() => {
  return Object.fromEntries(
    stips.value?.data.map((stip) => [stip.id, stip]) ?? []
  );
});

const currentOfferTemplateWfbRecord = ref<WfbOfferTemplateRecord>({
  id: props.offerTemplate.id,
  enabled: false,
  destinations: [],
});

const selectedStips = computed<StipDefinition[]>(() => {
  return localOfferTemplate.value.content.stips
    .map((stipId) => idToStipMap.value[stipId])
    .filter((stip): stip is StipDefinition => Boolean(stip));
});

const { fetchWrapper: getOfferTemplate, loading: isOfferTemplatesLoading } =
  usePromiseWrapper(async (id: string) => {
    const offerTemplatesResponse = await offerTemplateService.getTemplate(id);
    localOfferTemplate.value = offerTemplatesResponse.data;
  });

const {
  fetchWrapper: getCheckoutTemplatesForFunder,
  loading: isCheckoutTemplatesLoading,
} = usePromiseWrapper(async () => {
  if (!showCheckout.value || !props.funder) {
    return;
  }

  const { templates } = await checkoutTemplateService.getAll({
    funder_id: props.funder.id,
  });

  checkoutTemplatesForFunder.value = templates;
});

const { fetchWrapper: deleteOfferTemplate } = usePromiseWrapper(async () => {
  if (!props.isFunderList) {
    return;
  }
  await offerTemplateService.deleteTemplate(props.offerTemplate);
  emit("template-deleted", props.offerTemplate);
});

const isContentLoading = computed(
  () =>
    isOfferTemplatesLoading.value ||
    isStipsLoading.value ||
    isCheckoutTemplatesLoading.value
);

const updateOfferTemplateStips = async (stips: number[]) => {
  await offerTemplateService.updateTemplate({
    ...localOfferTemplate.value,
    content: {
      ...localOfferTemplate.value.content,
      stips,
    },
  });
  localOfferTemplate.value.content.stips = stips;
};

const offerTemplateFields = computed(() => {
  return localOfferTemplate.value.content.groups.flatMap((group) =>
    group.fields.map((field) => field.offer?.label ?? field.closing.label ?? "")
  );
});

const stipNames = computed(() => {
  return selectedStips.value.map((stip) => stip.name);
});

const showCheckout = computed(() => {
  if (!props.funder) {
    return false;
  }

  if (
    activeTemplate.value.category?.name !== "internal_funding" &&
    !props.isFunderList
  ) {
    return false;
  }

  return true;
});

const toggleSection = async () => {
  if (!canCustomiseWfbRecord.value) {
    return;
  }

  isSectionOpened.value = !isSectionOpened.value;

  if (isSectionOpened.value) {
    if (!localOfferTemplate.value.content) {
      await getOfferTemplate(props.offerTemplate.id);
    }
    if (!checkoutTemplatesForFunder.value.length) {
      await getCheckoutTemplatesForFunder();
    }
  }
};

const getWorkflowTemplates = async (params: SearchParams) => {
  const payload: WorkflowTemplatesPayloadParams = {
    active: "1",
    approved: "1",
    sort_by: "name",
    clients: props.clientId ? [props.clientId] : undefined,
    category: "internal_funding",
    ...params,
  };
  return await workflowApiService.getWorkflowTemplates(payload);
};

const setDefaultWFBTemplate = (template: IWorkflow | undefined) => {
  const offerTemplateId = template?.id
    ? currentOfferTemplateWfbRecord.value.destinations[0]?.offer_template
    : null;
  currentOfferTemplateWfbRecord.value.destinations[0] = {
    offer_template: offerTemplateId,
    workflow_template: template?.id,
  };
  if (!offerTemplateId) {
    selectedOfferTemplate.value = {
      name: "",
    };
  }

  dispatch("workflows/updateOfferTemplateRecord", {
    funderId: props.funder?.id,
    offerTemplate: currentOfferTemplateWfbRecord.value,
  });
};

const setDefaultOfferTemplate = (
  _: string | undefined,
  offerTemplateId: string
) => {
  const templateId = offerTemplateId === "0" ? undefined : offerTemplateId;
  currentOfferTemplateWfbRecord.value.destinations[0] = {
    offer_template: templateId,
    workflow_template:
      currentOfferTemplateWfbRecord.value.destinations[0]?.workflow_template,
  };
  dispatch("workflows/updateOfferTemplateRecord", {
    funderId: props.funder?.id,
    offerTemplate: currentOfferTemplateWfbRecord.value,
  });
};

const handleToggleTemplate = async (value: boolean) => {
  currentOfferTemplateWfbRecord.value.enabled = value;
  dispatch("workflows/updateOfferTemplateRecord", {
    funderId: props.funder?.id,
    offerTemplate: { ...currentOfferTemplateWfbRecord.value, enabled: value },
  });
};

const updateOfferTemplate = async (template: OfferTemplate) => {
  const updatedTemplate = await offerTemplateService.updateTemplate(template);

  localOfferTemplate.value = { ...updatedTemplate };
  showOfferFieldsModal.value = false;
};

const getPreselectedValues = async () => {
  const workflowTemplateId =
    currentOfferTemplateWfbRecord.value?.destinations[0]?.workflow_template;
  const clientOfferTemplateId =
    currentOfferTemplateWfbRecord.value?.destinations[0]?.offer_template;

  if (workflowTemplateId) {
    const workflowTemplate =
      await workflowApiService.getWorkflowTemplate(workflowTemplateId);
    slectedWorkflowTemplate.value = workflowTemplate;
  }
  if (clientOfferTemplateId) {
    const offerTemplate = (
      await offerTemplateService.getTemplate(clientOfferTemplateId)
    ).data;
    selectedOfferTemplate.value = offerTemplate;
  }
};

onMounted(() => {
  const template = props.funderWfbRecord?.offer_templates.find(
    (template) => template.id === props.offerTemplate.id
  );
  currentOfferTemplateWfbRecord.value = template ?? {
    id: props.offerTemplate.id,
    enabled: false,
    destinations: [],
  };
  if (props.isFunderList) {
    return;
  }
  getPreselectedValues();
});
</script>
